import { useState, useRef, useCallback, useEffect } from 'react';
import { getApiUrl, apiRequest, haySesionActiva } from '../config/api';
import { getChatKey } from '../utils/bots';

// Lógica de la lista de conversaciones extraída del Dashboard.
//
// Este hook es dueño del estado de `chats`, de los filtros del buscador, de la
// paginación y del debounce de recarga. Devuelve `setChats`, `reload` y
// `scheduleReload` para que los otros escritores del Dashboard (envío de
// mensajes, marcado de leídos y los callbacks del WebSocket) puedan seguir
// escribiendo en la lista con el mismo efecto de antes.
//
// El contenedor conserva `selectedBotId`, `selectedBotIdRef`, `activeTab`,
// `selectedChat` y `setSelectedChat` y los baja por parámetros porque los tocan
// varios destinos (mensajes, STOMP, historial); aquí solo se leen.
export function useChats({ selectedBotId, selectedBotIdRef, activeTab, selectedChat, setSelectedChat, puedeVerChatEnVivo }) {
  const [chats, setChats] = useState([]);

  // Filtros del buscador de chats (los que están en los inputs, aún sin aplicar)
  const [chatFilters, setChatFilters] = useState({ identificador: '', canal: '', nombre: '' });
  // Filtros aplicados (los que se envían al backend; se actualizan al pulsar Buscar)
  const [appliedChatFilters, setAppliedChatFilters] = useState({ identificador: '', canal: '', nombre: '' });
  const appliedChatFiltersRef = useRef({ identificador: '', canal: '', nombre: '' });
  // Canales (plataformas) disponibles para el bot seleccionado, en minúsculas
  // (ej: ["whatsapp", "telegram"]). Se usa para construir el desplegable de canal
  // del buscador de chats mostrando solo las opciones aplicables.
  const [availableChannels, setAvailableChannels] = useState([]);

  // Estado de paginación para chats
  const [chatsPagination, setChatsPagination] = useState({
    currentPage: 0,
    pageSize: 20,
    totalElements: 0,
    totalPages: 0,
    hasMore: false,
    isLoading: false
  });

  // Debounce del refetch de la lista de chats disparado por eventos WebSocket:
  // el WS puede entregar muchos mensajes seguidos y no queremos un GET /chats por
  // cada uno. Coalescemos las recargas en una ventana corta.
  const reloadChatsTimerRef = useRef(null);

  // Función para cargar chats con paginación
  const loadChats = useCallback(async (page = 0, size = 20, botIdFilter = null, filters = null) => {
    try {
      setChatsPagination(prev => ({ ...prev, isLoading: true }));

      let endpoint = `${getApiUrl('/chats')}?page=${page}&size=${size}`;
      if (botIdFilter) {
        endpoint += `&botId=${botIdFilter}`;
      }
      if (filters?.identificador) {
        endpoint += `&identificador=${encodeURIComponent(filters.identificador)}`;
      }
      if (filters?.canal) {
        endpoint += `&canal=${encodeURIComponent(filters.canal)}`;
      }
      if (filters?.nombre) {
        endpoint += `&nombre=${encodeURIComponent(filters.nombre)}`;
      }
      console.log(`📡 Cargando chats - Página ${page}, Tamaño ${size}${botIdFilter ? `, Bot: ${botIdFilter}` : ''}${filters && (filters.identificador || filters.canal || filters.nombre) ? `, Filtros: ${JSON.stringify(filters)}` : ''}`);

      const response = await apiRequest(endpoint);

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      // Manejar respuesta PagedResponse del backend
      const pagedData = data.content ? data : { content: data.chats || [], ...data };
      const chatsData = pagedData.content || [];

      console.log(`✅ Chats cargados: ${chatsData.length} de ${pagedData.totalElements || chatsData.length} totales`);
      console.log(`🔍 BotId filtro aplicado: ${botIdFilter}`);
      console.log(`🔍 BotIds de chats recibidos:`, chatsData.map(c => c.botId));

      // Formatear chats
      const formattedChats = chatsData.map(chat => ({
        ...chat,
        id: chat.chatId,
        avatar: chat.platform === 'telegram' ? 'TG' : chat.platform === 'instagram' ? 'IG' : 'WA',
        status: 'online',
        unread: chat.unreadCount || chat.unread || 0,
        messages: [], // Los mensajes se cargan cuando se selecciona el chat
        botId: chat.botId // Asegurar que botId se mantiene
      }));

      // Actualizar estado de paginación
      setChatsPagination({
        currentPage: pagedData.page || page,
        pageSize: pagedData.size || size,
        totalElements: pagedData.totalElements || chatsData.length,
        totalPages: pagedData.totalPages || Math.ceil((pagedData.totalElements || chatsData.length) / size),
        hasMore: pagedData.hasNext !== undefined ? pagedData.hasNext : (page < (pagedData.totalPages || 1) - 1),
        isLoading: false
      });

      return formattedChats;
    } catch (error) {
      console.error('❌ Error cargando chats:', error);
      setChatsPagination(prev => ({ ...prev, isLoading: false }));
      return [];
    }
  }, []);

  // Función para cargar más chats (siguiente página)
  const loadMoreChats = useCallback(async () => {
    if (chatsPagination.isLoading || !chatsPagination.hasMore) {
      return;
    }

    const nextPage = chatsPagination.currentPage + 1;
    console.log(`📜 Cargando página ${nextPage} de chats...`);

    console.log('[loadMoreChats] selectedBotIdRef:', selectedBotIdRef.current);
    const newChats = await loadChats(
      nextPage,
      chatsPagination.pageSize,
      selectedBotIdRef.current,
      appliedChatFiltersRef.current
    );

    if (newChats.length > 0) {
      // Agregar nuevos chats a la lista existente
      setChats(prevChats => {
        // Evitar duplicados
        const existingIds = new Set(prevChats.map(c => getChatKey(c)));
        console.log('[loadMoreChats] existingIds size:', existingIds.size);
        const uniqueNewChats = newChats.filter(c => !existingIds.has(getChatKey(c)));
        console.log('[loadMoreChats] newChats:', newChats.length, 'uniqueNewChats:', uniqueNewChats.length);
        return [...prevChats, ...uniqueNewChats];
      });
    }
  }, [chatsPagination, loadChats]); // eslint-disable-line react-hooks/exhaustive-deps

  // Aplica los filtros actuales del buscador y recarga la lista desde la página 0
  const handleSearchChats = useCallback(async () => {
    const filters = {
      identificador: chatFilters.identificador.trim(),
      canal: chatFilters.canal.trim(),
      nombre: chatFilters.nombre.trim()
    };
    setAppliedChatFilters(filters);
    appliedChatFiltersRef.current = filters;
    setChats([]);
    setChatsPagination(prev => ({ ...prev, currentPage: 0 }));
    const result = await loadChats(0, chatsPagination.pageSize, selectedBotIdRef.current, filters);
    setChats(result || []);
  }, [chatFilters, loadChats, chatsPagination.pageSize]); // eslint-disable-line react-hooks/exhaustive-deps

  // Limpia los filtros y recarga la lista completa
  const handleClearChatFilters = useCallback(async () => {
    const empty = { identificador: '', canal: '', nombre: '' };
    setChatFilters(empty);
    setAppliedChatFilters(empty);
    appliedChatFiltersRef.current = empty;
    setChats([]);
    setChatsPagination(prev => ({ ...prev, currentPage: 0 }));
    const result = await loadChats(0, chatsPagination.pageSize, selectedBotIdRef.current, empty);
    setChats(result || []);
  }, [loadChats, chatsPagination.pageSize]); // eslint-disable-line react-hooks/exhaustive-deps

  // Quita un filtro individual y recarga (usado por los chips)
  const handleRemoveChatFilter = useCallback(async (key) => {
    const next = {
      ...appliedChatFiltersRef.current,
      [key]: ''
    };
    setChatFilters(next);
    setAppliedChatFilters(next);
    appliedChatFiltersRef.current = next;
    setChats([]);
    setChatsPagination(prev => ({ ...prev, currentPage: 0 }));
    const result = await loadChats(0, chatsPagination.pageSize, selectedBotIdRef.current, next);
    setChats(result || []);
  }, [loadChats, chatsPagination.pageSize]); // eslint-disable-line react-hooks/exhaustive-deps

  // Función para recargar chats desde el backend (manteniendo mensajes ya cargados)
  const reloadChatsFromBackend = useCallback(async (preserveMessages = true) => {
    try {
      // El token de acceso vive en una cookie httpOnly; aquí solo comprobamos
      // el marcador de sesión para no recargar cuando no hay sesión. Si la
      // cookie hubiera caducado, `apiRequest` resuelve el 401 (refresh o login).
      if (!haySesionActiva()) {
        console.error("No hay sesión activa");
        return;
      }

      console.log("🔄 Recargando chats desde el backend...");

      // Recargar primera página de chats para mantener el orden correcto (con filtro de bot)
      console.log('[reloadChatsFromBackend] selectedBotIdRef:', selectedBotIdRef.current);
      const reloadedChats = await loadChats(
        0,
        chatsPagination.pageSize,
        selectedBotIdRef.current,
        appliedChatFiltersRef.current
      );

      // IMPORTANTE: aunque el backend devuelva 0 chats hay que reflejarlo en el
      // estado. Antes se hacía `if (reloadedChats.length > 0) setChats(...)`, lo
      // que dejaba la lista antigua visible cuando el filtro (p.ej. cambio de
      // bot) devolvía vacío — y provocaba que se vieran chats de otros bots
      // mientras el header decía "0 de 0 totales".
      if (preserveMessages) {
        setChats(prevChats => {
          const prevChatsMap = new Map(prevChats.map(chat => [getChatKey(chat), chat]));
          console.log('[reloadChatsFromBackend] prevChatsMap size:', prevChatsMap.size);

          const updatedChats = reloadedChats.map(chat => {
            const existingChat = prevChatsMap.get(getChatKey(chat));
            return {
              ...chat,
              id: chat.chatId,
              avatar: chat.platform === 'telegram' ? 'TG' : chat.platform === 'instagram' ? 'IG' : 'WA',
              status: existingChat?.status || 'online',
              messages: existingChat?.messages || [],
              unread: chat.unreadCount || chat.unread || 0
            };
          });
          console.log('[reloadChatsFromBackend] reloadedChats:', reloadedChats.length, 'updatedChats:', updatedChats.length);

          return updatedChats;
        });
      } else {
        setChats(reloadedChats);
      }

      console.log(`✅ Chats recargados: ${reloadedChats.length} (ya ordenados del backend)`);
    } catch (error) {
      console.error("Error recargando chats:", error);
    }
  }, [loadChats, chatsPagination.pageSize]); // eslint-disable-line react-hooks/exhaustive-deps

  // Versión "debounced" de reloadChatsFromBackend para eventos WebSocket: coalesce
  // ráfagas de mensajes en una sola recarga (evita un GET /chats por cada mensaje y
  // reduce drásticamente la carga con muchos chats/agentes activos).
  const scheduleReloadChats = useCallback((preserveMessages = true) => {
    if (reloadChatsTimerRef.current) {
      clearTimeout(reloadChatsTimerRef.current);
    }
    reloadChatsTimerRef.current = setTimeout(() => {
      reloadChatsTimerRef.current = null;
      reloadChatsFromBackend(preserveMessages);
    }, 600);
  }, [reloadChatsFromBackend]);

  // Recargar chats cuando cambia el bot seleccionado
  useEffect(() => {
    if (activeTab === 'conversaciones' && selectedBotId && puedeVerChatEnVivo) {
      setChats([]);
      setChatsPagination(prev => ({ ...prev, currentPage: 0 }));

      // Limpiar chat seleccionado si no pertenece al bot seleccionado
      if (selectedChat && selectedChat.botId !== selectedBotId) {
        setSelectedChat(null);
      }

      loadChats(0, chatsPagination.pageSize, selectedBotId, appliedChatFiltersRef.current).then(chats => {
        if (chats.length > 0) {
          setChats(chats);
        } else {
          setChats([]);
        }
      });
    }
  }, [selectedBotId, activeTab, puedeVerChatEnVivo]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    appliedChatFiltersRef.current = appliedChatFilters;
  }, [appliedChatFilters]);

  // Cargar los canales (plataformas) disponibles del bot seleccionado para
  // construir el desplegable del buscador. Si el canal actualmente filtrado
  // ya no está disponible para el nuevo bot, se descarta del filtro.
  useEffect(() => {
    if (!selectedBotId) {
      setAvailableChannels([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const response = await apiRequest(getApiUrl(`/bots/${selectedBotId}/channels`));
        if (!response.ok) {
          if (!cancelled) setAvailableChannels([]);
          return;
        }
        const data = await response.json();
        const channels = Array.isArray(data?.channels) ? data.channels.map(c => String(c).toLowerCase()) : [];
        if (cancelled) return;
        setAvailableChannels(channels);

        setChatFilters(f => (f.canal && !channels.includes(f.canal) ? { ...f, canal: '' } : f));
        if (appliedChatFiltersRef.current.canal && !channels.includes(appliedChatFiltersRef.current.canal)) {
          const cleared = { ...appliedChatFiltersRef.current, canal: '' };
          appliedChatFiltersRef.current = cleared;
          setAppliedChatFilters(cleared);
        }
      } catch (error) {
        console.error('Error cargando canales del bot:', error);
        if (!cancelled) setAvailableChannels([]);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedBotId]);

  return {
    chats,
    setChats,
    chatFilters,
    setChatFilters,
    appliedChatFilters,
    availableChannels,
    chatsPagination,
    loadChats,
    loadMoreChats,
    handleSearchChats,
    handleClearChatFilters,
    handleRemoveChatFilter,
    reload: reloadChatsFromBackend,
    scheduleReload: scheduleReloadChats,
  };
}

export default useChats;
